// @ts-nocheck
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/useOrganization';
import { Card, CardContent } from '@/components/ui/card';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import TeamSearchDialog, { TeamSearchMatch } from '@/components/teams/TeamSearchDialog';
import TeamDetailsForm, { TeamDetailsValue } from '@/components/teams/TeamDetailsForm';
import InviteCoachStep, { InviteCoachValue } from '@/components/teams/InviteCoachStep';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const FALLBACK_ORG_ID = '2bdb28dd-175e-4d24-91df-62b815ab734c'; // TODO: replace with real org context lookup

interface FormData extends TeamDetailsValue, InviteCoachValue {
  search_event_id: string | null;
}

const STEPS = [
  { n: 1, label: 'Find existing team' },
  { n: 2, label: 'Team details' },
  { n: 3, label: 'Invite coach' },
];

const SUBTITLES: Record<number, string> = {
  1: "First, let's check if your team is already in RLevents",
  2: 'Team details',
  3: 'Invite your head coach',
};

function Stepper({ step, onJump }: { step: number; onJump: (s: number) => void }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {STEPS.map((s, i) => {
        const isComplete = step > s.n;
        const isCurrent = step === s.n;
        const clickable = isComplete;
        return (
          <div key={s.n} className="flex items-center">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onJump(s.n)}
              className={cn(
                'flex items-center gap-2 transition-opacity',
                clickable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-center w-9 h-9 rounded-full border-2 text-sm font-semibold transition-colors',
                  isComplete && 'bg-primary border-primary text-primary-foreground',
                  isCurrent && 'bg-primary border-primary text-primary-foreground',
                  !isCurrent && !isComplete && 'bg-background border-muted-foreground/30 text-muted-foreground'
                )}
              >
                {isComplete ? <Check className="h-4 w-4" /> : s.n}
              </div>
              <span
                className={cn(
                  'hidden sm:inline text-sm',
                  isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground'
                )}
              >
                {s.label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <div className={cn('w-8 sm:w-12 h-0.5 mx-2', step > s.n ? 'bg-primary' : 'bg-muted-foreground/30')} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function NewTeamPage() {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const organizationId = organization?.id || FALLBACK_ORG_ID;

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    canonical_name: '',
    age_group: '',
    gender: '',
    home_state: '',
    home_city: '',
    primary_color: '#002E62',
    logo_file: null,
    logo_url: null,
    primary_coach_name: '',
    primary_coach_email: '',
    primary_coach_phone: '',
    send_invite_now: true,
    personal_message: '',
    search_event_id: null,
  });

  const patch = (p: Partial<FormData>) => setFormData((prev) => ({ ...prev, ...p }));

  const handleTeamSelected = (team: TeamSearchMatch) => {
    navigate(`/teams/${team.stable_slug}`);
  };

  const handleJoinRequested = async (teamId: string, message: string) => {
    try {
      const { error } = await invokeEdgeFunction('rle-create-or-claim-team', {
        body: {
          action: 'request_to_manage',
          team_id: teamId,
          message,
          organization_id: organizationId,
        },
      });
      if (error) throw error;
      toast.success('Request sent');
      const match = (window as any).__lastSearchMatch as TeamSearchMatch | undefined;
      if (match?.stable_slug) navigate(`/teams/${match.stable_slug}`);
    } catch (e: any) {
      toast.error(e.message || 'Failed to send request');
    }
  };

  const handleCreateNew = (query: string) => {
    patch({ canonical_name: query });
    setStep(2);
  };

  const handleSubmit = async (opts?: { skip?: boolean }) => {
    setSubmitting(true);
    try {
      const coachName = opts?.skip ? null : formData.primary_coach_name || null;
      const coachEmail = opts?.skip ? null : formData.primary_coach_email || null;

      const { data, error } = await invokeEdgeFunction('rle-create-or-claim-team', {
        body: {
          action: 'create_new',
          canonical_name: formData.canonical_name,
          age_group: formData.age_group,
          gender: formData.gender,
          home_state: formData.home_state || null,
          home_city: formData.home_city || null,
          primary_coach_name: coachName,
          primary_coach_email: coachEmail,
          organization_id: organizationId,
          search_event_id: formData.search_event_id,
        },
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error?.message || 'Failed to create team');

      const team = data.data.team;

      if (formData.logo_url) {
        await (supabase as any).from('teams').update({ logo_url: formData.logo_url }).eq('id', team.id);
      }

      if (!opts?.skip && formData.send_invite_now && formData.primary_coach_email) {
        await invokeEdgeFunction('rle-invite-team-member', {
          body: {
            team_id: team.id,
            invitee_email: formData.primary_coach_email,
            invitee_name: formData.primary_coach_name,
            role: 'head_coach',
            message: formData.personal_message,
            send_method: 'email',
          },
        });
      }

      toast.success('Team created!');
      navigate(`/teams/${team.stable_slug}`);
    } catch (e: any) {
      toast.error(e.message || 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-[720px] mx-auto">
        <h1 className="text-3xl font-bold text-center mb-2">Create a team</h1>

        <Stepper step={step} onJump={(s) => setStep(s as 1 | 2 | 3)} />

        <p className="text-center text-muted-foreground mb-6">{SUBTITLES[step]}</p>

        <Card>
          <CardContent className="p-6">
            {step === 1 && (
              <TeamSearchDialog
                open
                embedded
                onOpenChange={() => {}}
                organizationId={organizationId}
                onTeamSelected={handleTeamSelected}
                onJoinRequested={handleJoinRequested}
                onCreateNew={handleCreateNew}
              />
            )}

            {step === 2 && (
              <TeamDetailsForm
                value={formData}
                onChange={patch}
                onBack={() => setStep(1)}
                onContinue={() => setStep(3)}
              />
            )}

            {step === 3 && (
              <InviteCoachStep
                value={formData}
                onChange={patch}
                onBack={() => setStep(2)}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
